import { useEffect, useMemo, useState, useRef, useContext } from 'react';
import { Table, Header, HeaderRow, HeaderCell, Body, Row, Cell } from '@table-library/react-table-library/table';
import { useSort, SortToggleType } from '@table-library/react-table-library/sort';
import { useTheme as buildTheme } from '@table-library/react-table-library/theme';
import { usePagination } from '@table-library/react-table-library/pagination';
import { useErrorBoundary } from 'react-error-boundary';
import { Pagination } from './Pagination.js';
import { FavoriteDesignation } from './FavoriteDesignation.js';
import FavoriteFilter from './FavoriteFilter.js';
import { getConfigValue } from '../util/getConfigValue.js';
import { getSanitizedHtml } from '../util/getSanitizedHtml.js';
import { compareForDynamicTable } from '../util/dynamicTableSort.js';
import { getTableColumnsSpec } from '../util/getTableColumnsSpec.js';
import { hcpTab } from '../util/constants.js';
import { containsHtmlTags } from '../util/validation';
import { debounce } from '@mui/material';
import PropTypes from 'prop-types';
import { AppContext } from '../util/AppContext';

const setActiveIconColor = {
	color: 'white',
};

const noBottomBorder = {
	borderBottom: 'none',
}

const THEME = {
	Row: `
        &.row-select-selected,
        &.row-select-single-selected {
        font-weight: normal;
        }
  `,
	Cell: `
        & div 	{
          white-space: normal;
        }
  `,
};

SelectTargetComponent.propTypes = {
	filterFunction: PropTypes.func.isRequired,
	handleChange: PropTypes.func.isRequired,
	activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	showResults: PropTypes.bool.isRequired,
	setShowResults: PropTypes.func.isRequired,
	getPrescribers: PropTypes.func.isRequired,
	getGroupPractices: PropTypes.func.isRequired,
	getGeographyAggregates: PropTypes.func.isRequired,
	handleActive: PropTypes.func.isRequired,
	searchInput: PropTypes.string.isRequired,
	setSearchInput: PropTypes.func.isRequired,
	showMaxRecordsMessage: PropTypes.bool,
	delaySearch: PropTypes.bool,
};

function SelectTargetComponent({
	filterFunction,
	handleChange,
	activeId,
	showResults,
	setShowResults,
	getPrescribers,
	getGroupPractices,
	getGeographyAggregates,
	handleActive,
	searchInput,
	setSearchInput,
	showMaxRecordsMessage = false,
	delaySearch = false,
}) {
	const [appState, setAppState] = useContext(AppContext);
	const [prescribers, setPrescribers] = useState([]);
	const [prescriberHeaders, setPrescriberHeaders] = useState([]);
	const [groupPractices, setGroupPractices] = useState([]);
	const [groupPracticeHeaders, setGroupPracticeHeaders] = useState([]);
	const [geographyAggregates, setGeographyAggregates] = useState([]);
	const [geographyAggregateHeaders, setGeographyAggregateHeaders] = useState([]);
	const [headers, setHeaders] = useState([]);
	const [data, setData] = useState({ nodes: [] });
	const [previousSearchInput, setPreviousSearchInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [numberOfPages, setNumberOfPages] = useState(0);
	const [themeColumns, setThemeColumns] = useState(null);
	const [prescribersLoaded, setPrescribersLoaded] = useState(false);
	const [groupPracticesLoaded, setGroupPracticesLoaded] = useState(false);
	const [geographyAggregatesLoaded, setGeographyAggregatesLoaded] = useState(false);
	const [activeTab, setActiveTab] = useState('0'); // tabs: 1, 2, 3
	const [showGroupPracticeTab, setShowGroupPracticeTab] = useState(false);
	const [showPrescriberTab, setShowPrescriberTab] = useState(false);
	const [showGeographyAggregateTab, setShowGeographyAggregateTab] = useState(false);
	const [maxRecordsMessage, setMaxRecordsMessage] = useState();
	const [validationError, setValidationError] = useState(null);
	const canDisplayRecentWinIndicator = getConfigValue(appState?.config?.Target?.List?.DisplayRecentWins?.Enabled, appState)?.Value === 'true';

	const containsHtmlMessage = getConfigValue(appState?.config?.Common?.Validation?.Message?.ContainsHtml, appState)?.Value || null;
	let maxRecords = 100;
	if (getConfigValue(appState?.config?.Target?.List?.MaxRecordsToDisplay, appState)?.Value) {
		maxRecords = parseInt(getConfigValue(appState?.config?.Target?.List?.MaxRecordsToDisplay, appState)?.Value, 10);
	}

	const groupPracticeEnabled = useMemo(() => getConfigValue(appState.config.GroupPractice?.Enabled, appState)?.Value === 'true', [appState]);
	const geographyAggregateEnabled = useMemo(() => getConfigValue(appState.config.GeographyAggregate?.Enabled, appState)?.Value === 'true', [appState]);

	const hcpFavoritesEnabled = getConfigValue(appState.config.HCP?.Favorites?.Enabled, appState)?.Value === 'true';
	const [hcpFavoriteCheckbox, setHcpFavoriteCheckbox] = useState(getConfigValue(appState.config.HCP?.Favorites?.Filter?.Default, appState)?.Value === 'true');
	const [previousHcpFavoriteCheckbox, setPreviousHcpFavoriteCheckbox] = useState(
		getConfigValue(appState.config.HCP?.Favorites?.Filter?.Default, appState)?.Value === 'true',
	);

	const featureFlag_hideTabsWhenNoData = getConfigValue(appState.config.CreateSalesAid?.Features?.HideTabsWhenNoData, appState)?.Value === 'true';

	const { showBoundary } = useErrorBoundary();

	const pagination = usePagination(data, {
		state: {
			page: 0,
			size: appState.config?.Target?.RecordsPerPage?.Value,
		},
	});

	const executeFilter = async (data, search, favoriteOnly, activeTab) => {
		filterFunction(data, search, favoriteOnly, activeTab)
			.then((filteredData) => {
				let length = filteredData.length;
				setData({ nodes: filteredData });
				setShowResults(length > 0);

				if (searchInput !== previousSearchInput || hcpFavoriteCheckbox !== previousHcpFavoriteCheckbox) {
					pagination.fns.onSetPage(0);
					setPreviousSearchInput(searchInput);
					setPreviousHcpFavoriteCheckbox(hcpFavoriteCheckbox);
				}

				const pageSize = parseInt(appState.config?.Target?.RecordsPerPage?.Value, 10);
				let pages = Math.floor(length / pageSize);
				pages = length % pageSize > 0 ? pages + 1 : pages;
				setNumberOfPages(pages);
				if (pagination.state.page >= pages) {
					pagination.fns.onSetPage(pages === 0 ? 0 : pages - 1);
				}

				if (showMaxRecordsMessage && length === maxRecords) {
					setMaxRecordsMessage(getConfigValue(appState?.config?.Target?.List?.MaxRecordsMessage, appState)?.Value);
				} else {
					setMaxRecordsMessage(null);
				}
				setIsLoading(false);
			})
			.catch((error) => {
				showBoundary(error);
			});
	};

	//Define debounced search function so the API is not called on every keystroke but only after a delay.
	const debouncedSearch = useRef(
		debounce((data, filter, favoriteOnly, activeTab) => {
			executeFilter(data, filter, favoriteOnly, activeTab);
		}, 300),
	).current;

	function getActiveTabData() {
		let data = [];
		let headers = [];
		if (activeTab === hcpTab.Prescriber) {
			data = prescribers;
			headers = prescriberHeaders;
		} else if (activeTab === hcpTab.GroupPractice) {
			data = groupPractices;
			headers = groupPracticeHeaders;
		} else if (activeTab === hcpTab.GeographyAggregate) {
			data = geographyAggregates;
			headers = geographyAggregateHeaders;
		}

		return { headers, data };
	}

	useEffect(() => {
		if (activeTab === '0') {
			setIsLoading(true);
			getPrescribers(searchInput, hcpFavoriteCheckbox)
				.catch((error) => {
					showBoundary(error);
				})
				.then((results) => {
					if (results?.rows?.length > 0) {
						setPrescribers(results.rows);
						setPrescriberHeaders(results.headers);
						setShowPrescriberTab(true);
					}
				})
				.finally(() => {
					setPrescribersLoaded(true);
				});

			if (groupPracticeEnabled) {
				getGroupPractices(searchInput, hcpFavoriteCheckbox)
					.catch((error) => {
						showBoundary(error);
					})
					.then((results) => {
						if (results?.rows?.length > 0) {
							setGroupPractices(results.rows);
							setGroupPracticeHeaders(results.headers);
							setShowGroupPracticeTab(true);
						}
					})
					.finally(() => {
						setGroupPracticesLoaded(true);
					});
			}

			if (geographyAggregateEnabled) {
				getGeographyAggregates(searchInput, hcpFavoriteCheckbox)
					.catch((error) => {
						showBoundary(error);
					})
					.then((results) => {
						if (results?.rows?.length > 0) {
							setGeographyAggregates(results.rows);
							setGeographyAggregateHeaders(results.headers);
							setShowGeographyAggregateTab(true);
						}
					})
					.finally(() => {
						setGeographyAggregatesLoaded(true);
					});
			}

			if (!featureFlag_hideTabsWhenNoData) {
				setShowPrescriberTab(true);
				setShowGroupPracticeTab(groupPracticeEnabled);
				setShowGeographyAggregateTab(geographyAggregateEnabled);
			}
		}

		setSearchInput('');
		pagination.fns.onSetPage(0);
	}, [appState?.market, appState?.territory, activeTab, groupPracticeEnabled, geographyAggregateEnabled]);

	useEffect(() => {
		const groupPracticeCheck = !groupPracticeEnabled || groupPracticesLoaded;
		const geographyAggregateCheck = !geographyAggregateEnabled || geographyAggregatesLoaded;
		if (prescribersLoaded && groupPracticeCheck && geographyAggregateCheck && activeTab === '0') {
			if (showPrescriberTab) {
				setActiveTab(hcpTab.Prescriber);
			} else if (showGroupPracticeTab) {
				setActiveTab(hcpTab.GroupPractice);
			} else if (showGeographyAggregateTab) {
				setActiveTab(hcpTab.GeographyAggregate);
			}
		}
		if (prescribersLoaded && groupPracticeCheck && geographyAggregateCheck) {
			setIsLoading(false);
		}
	}, [
		showGroupPracticeTab,
		showPrescriberTab,
		showGeographyAggregateTab,
		prescribersLoaded,
		groupPracticesLoaded,
		groupPracticeEnabled,
		geographyAggregatesLoaded,
		geographyAggregateEnabled,
	]);

	useEffect(() => {
		setIsLoading(true);
		const activeData = getActiveTabData();
		setHeaders(activeData.headers);
		if (delaySearch) {
			debouncedSearch(activeData.data, searchInput, hcpFavoritesEnabled && hcpFavoriteCheckbox, activeTab);
		} else {
			executeFilter(activeData.data, searchInput, hcpFavoritesEnabled && hcpFavoriteCheckbox, activeTab);
		}
	}, [searchInput, prescribers, groupPractices, geographyAggregates, activeTab, hcpFavoriteCheckbox, delaySearch]);

	//build sort functions object
	const sortFunctions = {};
	for (const header of headers) {
		sortFunctions[header.key] = (array) => array.sort((a, b) => compareForDynamicTable(header.key, header.sortField, false, a, b));
	}
	let defaultSortKey = 'Name';
	if (headers && headers.length > 0) {
		defaultSortKey = headers[0].key;
	}

	const sort = useSort(
		data,
		{
			state: {
				sortKey: defaultSortKey,
				reverse: false,
			},
		},
		{
			sortFns: sortFunctions,
			sortToggleType: SortToggleType.AlternateWithReset,
		},
	);

	useEffect(() => {
		const columnsSpec = getTableColumnsSpec(headers);
		const themeObject = buildTheme({
			...THEME,
			Table: `
            --data-table-library_grid-template-columns: 10% ${columnsSpec};
        `,
		});
		setThemeColumns(themeObject);
	}, [headers]);

	const getIcon = (sortKey) => {
		if (sort.state.sortKey === sortKey && sort.state.reverse) {
			return <span className='fa-solid fa-arrow-down-wide-short' style={setActiveIconColor}></span>;
		}

		if (sort.state.sortKey === sortKey && !sort.state.reverse) {
			return <span className='fa-solid fa-arrow-up-short-wide' style={setActiveIconColor}></span>;
		}

		return <span className='fa-solid fa-sort' />;
	};

	function checkIdMatch(id) {
		return id === activeId;
	}

	const updateHcps = function (prevState, hcp) {
		return prevState.map((p) => {
			if (p.data.id === hcp.id) {
				return { ...p, data: { ...p.data, favorite: !hcp.favorite } };
			}
			return p;
		});
	};

	function handleFavoriteClick(hcp) {
		if (activeTab === hcpTab.Prescriber) {
			setPrescribers((prevState) => {
				return updateHcps(prevState, hcp);
			});
		} else if (activeTab === hcpTab.GroupPractice) {
			setGroupPractices((prevState) => {
				return updateHcps(prevState, hcp);
			});
		} else if (activeTab === hcpTab.GeographyAggregate) {
			setGeographyAggregates((prevState) => {
				return updateHcps(prevState, hcp);
			});
		}

		if (appState?.prescriber?.id === hcp.id) {
			setAppState({
				...appState,
				prescriber: { ...appState.prescriber, favorite: !hcp.favorite },
			});
		}
	}

	const handleChangeLocal = (event) => {
		const search = event.target.value.trim();
		if (containsHtmlTags(search)) {
			setValidationError(containsHtmlMessage);
			return;
		}
		setValidationError(null);
		handleChange(event);
	};

	return (
		<div className='card pd-card'>
			<div className='container'>
				<div className='row justify-content-center'>
					<div className='col-12'>
						<ul className='nav tabs tabs-stretched mt-3' role='tablist'>
							{showPrescriberTab && (
								<li className='nav-item' role='none' onClick={() => setActiveTab(hcpTab.Prescriber)}>
									<button
										className={`nav-link ${activeTab === hcpTab.Prescriber ? 'active' : ''}`}
										id='tab-group'
										data-bs-toggle='tab'
										data-bs-target='#tab-hcp-pane'
										type='button'
										role='tab'
										aria-controls='tab-hcp-pane'
										aria-selected='true'
									>
										<div className='fa-solid fa-user-doctor tab-icon'></div>{' '}
										<span className='d-none d-md-inline'>{getConfigValue(appState.config?.Target?.SelectHCP?.Title, appState)?.Value}</span>
									</button>
								</li>
							)}
							{showGroupPracticeTab && (
								<li className='nav-item' role='none' onClick={() => setActiveTab(hcpTab.GroupPractice)}>
									<button
										className={`nav-link ${activeTab === hcpTab.GroupPractice ? 'active' : ''}`}
										id='tab-group'
										data-bs-toggle='tab'
										data-bs-target='#tab-group-pane'
										type='button'
										role='tab'
										aria-controls='tab-group-pane'
										aria-selected='true'
									>
										<div className='fa-solid fa-hospital tab-icon'></div>{' '}
										<span className='d-none d-md-inline'>
											{getConfigValue(appState.config?.Target?.SelectGroupPractice?.Title, appState)?.Value}{' '}
										</span>
									</button>
								</li>
							)}
							{showGeographyAggregateTab && (
								<li className='nav-item' role='none' onClick={() => setActiveTab(hcpTab.GeographyAggregate)}>
									<button
										className={`nav-link ${activeTab === hcpTab.GeographyAggregate ? 'active' : ''}`}
										id='tab-geography-aggregate'
										data-bs-toggle='tab'
										data-bs-target='#tab-geography-aggregate-pane'
										type='button'
										role='tab'
										aria-controls='tab-geography-aggregate-pane'
										aria-selected='true'
									>
										<div className='fa-solid fa-location-dot tab-icon'></div>{' '}
										<span className='d-none d-md-inline'>
											{getConfigValue(appState.config?.Target?.SelectGeographyAggregate?.Title, appState)?.Value}{' '}
										</span>
									</button>
								</li>
							)}
						</ul>

						<FavoriteFilter hcpFavoriteCheckbox={hcpFavoriteCheckbox} setHcpFavoriteCheckbox={setHcpFavoriteCheckbox} includeWrappingRow={true} />

						{showMaxRecordsMessage && maxRecordsMessage && (
							<div>
								<div className='col-12 p-0'>
									<div className='alert alert-primary' role='alert'>
										<div className='alert-content'>
											<p>{maxRecordsMessage}</p>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* SEARCH FIELD */}
						<div className='row my-4'>
							<div className='col-12'>
								<div className='input-group'>
									<input
										className='form-control icon-search'
										type='text'
										placeholder='Search'
										value={searchInput}
										onChange={handleChangeLocal}
									></input>
									{validationError && <div className='invalid-feedback d-block'>{validationError}</div>}
								</div>
							</div>
						</div>

						{!isLoading && showResults && headers.length > 0 && (
							<Table
								className='table pd-table pod-table-hcp'
								key={headers.length}
								data={data}
								sort={sort}
								theme={themeColumns}
								layout={{ custom: true, isDiv: true }}
								pagination={pagination}
							>
								{(tableList) => (
									<>
										<Header>
											<HeaderRow className='table-dark d-none d-md-contents'>
												<HeaderCell className='px-2 py-2' scope='col'></HeaderCell>
												{headers.map((header) => (
													<HeaderCell key={header.key} scope='col'>
														{header.title}
														<button
															className='btn-ghost btn-table'
															onClick={() =>
																sort.fns.onToggleSort({
																	sortKey: header.key,
																})
															}
														>
															{getIcon(header.key)}
														</button>
													</HeaderCell>
												))}
											</HeaderRow>
										</Header>
										<Body>
											{tableList.map((item) => (
												<Row
													key={item.data.id}
													item={item}
													onClick={() => {
														if (!hcpFavoritesEnabled) {
															handleActive(item.data, activeTab);
														}
													}}
													className={activeId === item.data.id ? 'table-active' : ''}
												>
													<Cell className={canDisplayRecentWinIndicator === true && item.data.hasWinPlans === true ? 'pd-plan-table-row pd-plan-table-indicator-success' : ''} style={noBottomBorder}>
														<div className='input-container'>
															<input
																className='form-check-input'
																type='radio'
																name='selectHCP'
																id={'selectHCP' + item.data.id}
																checked={checkIdMatch(item.data.id)}
																onChange={() => {
																	handleActive(item.data, activeTab);
																}}
															></input>
															<FavoriteDesignation hcp={item.data} updateCallback={() => handleFavoriteClick(item.data)} />
														</div>
													</Cell>
													{Object.keys(item.columns).map((key) => (
														<Cell key={key}>
															{
																<span className='d-block d-md-none color-grey-d1'>
																	{item.columns[key] ? headers.find((h) => h.key === key)?.title : ''}
																</span>
															}
															<span
																className='form-check-label'
																htmlFor={'selectHCP' + item.data.id}
																dangerouslySetInnerHTML={getSanitizedHtml(item.columns[key])}
															></span>
														</Cell>
													))}
												</Row>
											))}
										</Body>
									</>
								)}
							</Table>
						)}

						{!isLoading && !showResults && (
							<div className='color-grey-d1 pb-4'>{getConfigValue(appState.config?.Common?.NoResultsFound, appState)?.Value}</div>
						)}
						{isLoading && <div className='color-grey-d1 pb-4'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>}
					</div>
				</div>
			</div>

			{!isLoading && numberOfPages > 1 && pagination && (
				<Pagination
					numberOfPages={pagination.state.getTotalPages(data.nodes)}
					currentPage={pagination.state.page}
					setCurrentPage={(index) => {
						pagination.fns.onSetPage(index);
					}}
				/>
			)}
		</div>
	);
}

export default SelectTargetComponent;
